import React, { useState } from 'react';
import { Card, CardBody, CardHeader, Col, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup, Alert, InputGroup, InputGroupText } from 'reactstrap';
import { H5 } from '../../../AbstractElements';
import { Edit, Trash2, RefreshCw, Download, Search } from 'react-feather';
import { fetchDelUser, fetchUpdateUser, fetchExportUser } from '../../../api/user';

const UserRVOPVTable = ({ usersRVOPV = [], loading, onRefresh }) => {
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      phone: user.phone || '',
      email: user.email || ''
    });
    setEditModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setDeleteModal(true);
  };

  const handleUpdate = async () => {
    try {
      if (!selectedUser) return;
      await fetchUpdateUser(selectedUser.id, formData);
      setEditModal(false);
      setSelectedUser(null);
      if (onRefresh) onRefresh();
      alert('Cập nhật user thành công!');
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Lỗi khi cập nhật user: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDelete = async () => {
    try {
      if (!selectedUser) return;
      await fetchDelUser(selectedUser.id);
      setDeleteModal(false);
      setSelectedUser(null);
      if (onRefresh) onRefresh();
      alert('Xóa user thành công!');
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Lỗi khi xóa user: ' + (err.message || 'Unknown error'));
    }
  };

  const handleExport = async () => {
    try {
      const today = new Date();
      const startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
      const endDate = today.toISOString().split('T')[0];
      await fetchExportUser('ALL', startDate, endDate);
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Lỗi khi xuất dữ liệu: ' + (err.message || 'Unknown error'));
    }
  };

  // Filter users based on search term
  const filteredUsers = usersRVOPV.filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
      (user.id && user.id.toString().includes(searchLower))
    );
  });

  return (
    <Col xxl="12" className="box-col-12">
      <Card>
        <CardHeader className="card-no-border">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <H5 className="mb-0">Danh Sách User RVOPV</H5>
            <div className="d-flex gap-2">
              <Button color="outline-success" size="sm" onClick={handleExport}>
                <Download size={16} className="me-1" />
                Export Excel
              </Button>
              {onRefresh && (
                <Button color="outline-primary" size="sm" onClick={onRefresh}>
                  <RefreshCw size={16} className="me-1" />
                  Làm mới
                </Button>
              )}
            </div>
          </div>
          <InputGroup>
            <InputGroupText>
              <Search size={16} />
            </InputGroupText>
            <Input
              type="text"
              placeholder="Tìm kiếm theo tên, email, SĐT, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-4">
              <p>Đang tải dữ liệu...</p>
            </div>
          ) : usersRVOPV.length === 0 ? (
            <Alert color="info">Không có dữ liệu user RVOPV</Alert>
          ) : filteredUsers.length === 0 ? (
            <Alert color="warning">
              <i className="fa fa-search me-2"></i>
              Không tìm thấy kết quả phù hợp với "{searchTerm}"
            </Alert>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Avatar</th>
                    <th>Tên</th>
                    <th>Email</th>
                    <th>Số Điện Thoại</th>
                    <th>Trạng Thái</th>
                    <th>Ngày Tạo</th>
                    <th>Hành Động</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        {user.avatar ? (
                          <img 
                            src={user.avatar} 
                            alt={`${user.name || 'User'} avatar`}
                            className="rounded-circle"
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.src = '/src/assets/images/dashboard/profile.png';
                            }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                            style={{ width: '40px', height: '40px' }}
                          >
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                        )}
                      </td>
                      <td>{user.name || 'N/A'}</td>
                      <td>{user.email || 'N/A'}</td>
                      <td>{user.phone || 'N/A'}</td>
                      <td>
                        <Badge color={user.isActive !== false ? 'success' : 'danger'}>
                          {user.isActive !== false ? 'Hoạt động' : 'Không hoạt động'}
                        </Badge>
                      </td>
                      <td>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString('vi-VN') : 'N/A'}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button color="outline-warning" size="sm" onClick={() => openEditModal(user)}>
                            <Edit size={14} />
                          </Button>
                          <Button color="outline-danger" size="sm" onClick={() => openDeleteModal(user)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal Sửa */}
      <Modal isOpen={editModal} toggle={() => setEditModal(!editModal)}>
        <ModalHeader toggle={() => setEditModal(!editModal)}>Sửa User RVOPV</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Tên</Label>
            <Input type="text" name="name" value={formData.name} onChange={handleInputChange} />
          </FormGroup>
          <FormGroup>
            <Label>Email</Label>
            <Input type="email" name="email" value={formData.email} onChange={handleInputChange} />
          </FormGroup>
          <FormGroup>
            <Label>Số Điện Thoại</Label>
            <Input type="text" name="phone" value={formData.phone} onChange={handleInputChange} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModal(false)}>Hủy</Button>
          <Button color="warning" onClick={handleUpdate}>Cập Nhật</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Xóa */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Xác Nhận Xóa</ModalHeader>
        <ModalBody>
          <p>Bạn có chắc chắn muốn xóa user <strong>{selectedUser?.name}</strong>?</p>
          <Alert color="warning">
            <strong>Cảnh báo:</strong> Hành động này không thể hoàn tác!
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>Hủy</Button>
          <Button color="danger" onClick={handleDelete}>Xóa</Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
};

export default UserRVOPVTable;
